import { makeAutoObservable } from 'mobx';
import {
  configure,
  DataCaptureContext,
  Camera,
  DataCaptureView,
  FrameSourceState,
  Logger,
  ImageFrameSource,
} from '@scandit/web-datacapture-core';
import {
  DriverLicense,
  IdCapture,
  idCaptureLoader,
  IdCaptureOverlay,
  IdCaptureSettings,
  IdCard,
  IdImageType,
  Passport,
  Region,
  SingleSideScanner,
} from '@scandit/web-datacapture-id';

export enum IdDocumentRecognitionSDKStatus {
  Uninitialized = 'uninitialized',
  Initializing = 'initializing',
  Ready = 'ready',
  Error = 'error',
}

class IdDocumentRecognitionStore {
  dataCaptureContext: DataCaptureContext | null;
  camera: Camera | null;
  idCapture: IdCapture | null;
  dataCaptureView: DataCaptureView | null;
  status: IdDocumentRecognitionSDKStatus;

  private _isScanning: boolean;
  private _isElementAttached: boolean;

  constructor() {
    makeAutoObservable(this);
    this.dataCaptureContext = null;
    this.camera = null;
    this.idCapture = null;
    this.dataCaptureView = null;
    this._isScanning = false;
    this._isElementAttached = false;

    this.status = IdDocumentRecognitionSDKStatus.Uninitialized;
  }

  async init(licenseKey: string | null, libraryLocation: string | null) {
    if (this.status !== IdDocumentRecognitionSDKStatus.Uninitialized) {
      return;
    }

    if (!licenseKey || !libraryLocation) {
      return;
    }
    this.setStatus(IdDocumentRecognitionSDKStatus.Initializing);

    try {
      await configure({
        licenseKey: licenseKey,
        libraryLocation: libraryLocation,
        moduleLoaders: [idCaptureLoader({ enableVIZDocuments: true })],
        logLevel: Logger.Level.Warn,
      });

      this.dataCaptureContext = await DataCaptureContext.create();
      this.camera = Camera.default;
      await this.camera.applySettings(IdCapture.recommendedCameraSettings);
      await this.dataCaptureContext.setFrameSource(this.camera);

      const idCaptureSettings = new IdCaptureSettings();

      idCaptureSettings.acceptedDocuments.push(new DriverLicense(Region.Any));
      idCaptureSettings.acceptedDocuments.push(new IdCard(Region.Any));
      idCaptureSettings.acceptedDocuments.push(new Passport(Region.Any));
      idCaptureSettings.setShouldPassImageTypeToResult(IdImageType.CroppedDocument, true);
      idCaptureSettings.setShouldPassImageTypeToResult(IdImageType.Face, true);
      idCaptureSettings.setShouldPassImageTypeToResult(IdImageType.Frame, true);

      idCaptureSettings.scannerType = new SingleSideScanner(true, true, true);

      this.idCapture = await IdCapture.forContext(this.dataCaptureContext, idCaptureSettings);
      this.dataCaptureView = await DataCaptureView.forContext(this.dataCaptureContext);

      this.setStatus(IdDocumentRecognitionSDKStatus.Ready);
    } catch (error) {
      console.log(error);
      this.setStatus(IdDocumentRecognitionSDKStatus.Error);
    }
  }

  async enableScanning(enabled: boolean) {
    if (this.status !== IdDocumentRecognitionSDKStatus.Ready || this._isScanning === enabled) {
      return;
    }

    this.dataCaptureView?.setProgressBarMessage('');
    this.dataCaptureView?.showProgressBar();

    this._isScanning = enabled;
    await this.dataCaptureContext?.frameSource?.switchToDesiredState(enabled ? FrameSourceState.On : FrameSourceState.Off);
    this.idCapture?.setEnabled(enabled);

    this.dataCaptureView?.hideProgressBar();
  }

  detachElement() {
    if (this._isElementAttached) {
      this.dataCaptureView?.detachFromElement();
      this._isElementAttached = false;
    }
  }

  async connectToElement(htmlElement: HTMLElement) {
    if (!this.dataCaptureView || !this.idCapture) {
      return;
    }

    this.dataCaptureView.connectToElement(htmlElement);
    await IdCaptureOverlay.withIdCaptureForView(this.idCapture, this.dataCaptureView);

    this._isElementAttached = true;
  }

  async cleanup() {
    this.detachElement();
    await this.idCapture?.setEnabled(false);
    await this.dataCaptureContext?.frameSource?.switchToDesiredState(FrameSourceState.Off);
    await this.dataCaptureContext?.removeAllModes();
    await this.dataCaptureContext?.dispose();

    this.setStatus(IdDocumentRecognitionSDKStatus.Uninitialized);

    this._isScanning = false;
  }

  setStatus(status: IdDocumentRecognitionSDKStatus) {
    this.status = status;
  }

  async scanStream(videoRef: any) {
    if (!videoRef?.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx!.drawImage(video, 0, 0, canvas.width, canvas.height);

    try {
      const imageData = ctx!.getImageData(0, 0, canvas.width, canvas.height);

      let source = await ImageFrameSource.fromImageData(imageData);
      await this.dataCaptureContext!.setFrameSource(source!);
      this.dataCaptureContext?.frameSource?.switchToDesiredState(FrameSourceState.On);
    } catch (error) {
      console.log(error);
    }
  }
}

const idDocumentRecognitionContext = new IdDocumentRecognitionStore();
export default idDocumentRecognitionContext;
